import { autoinject, transient, BindingEngine, useView } from "aurelia-framework";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { VesselOperationalMetricsDetails, VesselCiiHistory, CiiStatusEnum } from "../../../../../../models/vessels/vessel.models";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { I18N } from "aurelia-i18n";
import { DataType, SourceType } from "../../../../../../models/queries/queries.models";
import * as moment from "moment";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { EstimatedCiiRatingChartViewModel } from "./estimated-cii-rating-chart-view-model";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import { GetEstimatedCiiResponse } from "../../../../../../models/ghgs/ghg.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';

@autoinject
@transient()
@useView('views/pages/modules/vessels/steps/vessel-cii-rating/vessel-cii-rating.html')
export class VesselCiiRating extends CustomGridPageBase {
        
    private _customerType: string;
    private _liveCiiIconTimeout: number;
    private _liveCiiChartTimeout: number;

    operationalMetricsDetails: VesselOperationalMetricsDetails;
    vesselCiiHistories: VesselCiiHistory[];
    estimatedCiiRatingChartViewModel: EstimatedCiiRatingChartViewModel;
    estimatedCii: GetEstimatedCiiResponse;
    vesselId: number;
    
    public canViewLiveCII: boolean = false;
    public liveCiiLoading: boolean;
    public operationalMetricsDetailsLoading: boolean;
    
    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private vesselService: ManageVesselService,
        private ghgService: GhgService, private configService: ConfigService,
        private userClaimsService: UserClaimsService, private googleAnalyticsService: GoogleAnalyticsService) {
        super(i18N, 'Vessel', bindingEngine);
    }

    async activate(model: any) {
        this.canViewLiveCII = this.userClaimsService.hasPermission(Claims.CanViewLiveCII);
        this.vesselId = model?.vesselId;
        this._customerType = model?.customerType;
    }

    async attached() {
        try {
            if (this.vesselId) {
                this.liveCiiLoading = true;
                this.operationalMetricsDetailsLoading = true;

                await this.vesselService.getVesselOperationalMetrics(this.vesselId).then((response) => {
                    this.operationalMetricsDetails = response.operationalMetricsDetails ?? null;
                    this.vesselCiiHistories = response.vesselCiiHistories;
                    this.operationalMetricsDetailsLoading = false;
                });
                
                //Get estimated CII
                await this.ghgService.getEstimatedCii(this.vesselId).then((response) => {
                    if (response && response.historicalCiiRatings) {
                        this.estimatedCii = response;
                    }
                    this.liveCiiLoading = false;
                });
                
                this.setItemSource();

                this.estimatedCiiRatingChartViewModel = new EstimatedCiiRatingChartViewModel(this.configService, this.estimatedCii, this.googleAnalyticsService, this._customerType);
            }
        } catch {
            this.operationalMetricsDetailsLoading = false;
            this.liveCiiLoading = false;
        }


        //Send ga event
        this.googleAnalyticsService.sendCustomGA4EventMulti('vessel_cii_rating_page_view', {
            'customerType': this._customerType
        });
    }

    //Set item source for Cii rating history table
    setItemSource() {
        this.itemSource = {
            columns: [
                {
                    sortOrder: 1,
                    columnOrder: 1,
                    dataType: DataType.Default,
                    name: "ciiRatingYear",
                    title: "Rating Year"
                },
                {
                    sortOrder: 2,
                    columnOrder: 2,
                    dataType: DataType.CustomTemplate,
                    name: "ciiRating",
                    title: 'CII Rating',
                    value: '<div class="rating row align-items-middle ${item.ciiRating.toLowerCase()}">'+
                                '<comp-icon class="icon-cii-rating" icon="cii-rating"></comp-icon>'+
                                '<span class="cii-value">${item.ciiRating}</span>'+
                                '<span class="certified">${getStatus(item.ciiStatusId)}</span>'+
                            '</div>',
                    instancePerRow: true,
                    parameter: {
                        item: null,
                        getStatus: (status: number) => CiiStatusEnum[status]
                    },
                },
                {
                    sortOrder: 3,
                    columnOrder: 3,
                    dataType: DataType.Default,
                    name: "attainedCii",
                    title: 'Attanained CII <br /> (g CO<sub>2</sub>  per tonne nautical mile)',
                    valueConverter: (x) => x.toFixed(2),
                },
                {
                    sortOrder: 4,
                    columnOrder: 4,
                    dataType: DataType.Default,
                    name: "complianceIssueDate",
                    title: "Statement of Compliance <br /> Issue Date",
                    valueConverter: (date: Date | string) => date ? moment(date).format('DD/MM/YYYY') : '',
                },
            ],
            source: this.vesselCiiHistories, 
            sourceType: SourceType.Collection,
        }
    }

    getInTouch() {
        window.open('https://rightship.com/platform-cii-enquiries', '_blank').focus(); 
    }

    //#region Google analytics tracking

    onLiveCiiIconHover() {

        //Send ga event after 500ms of hover 
        this._liveCiiIconTimeout = window.setTimeout(() => {
            this.googleAnalyticsService.sendCustomGA4EventMulti('live_cii_tooltip_hover', {
                'customerType': this._customerType
            });
        }, 500);
    }
    
    liveCiiIconMouseOut() {

        //Clear timeout
        if (this._liveCiiIconTimeout !== null){
            clearTimeout(this._liveCiiIconTimeout);
            this._liveCiiIconTimeout = null;
        }
    }

    onLiveCiiChartHover() {

        //Send ga event after 500ms of hover
        this._liveCiiChartTimeout = window.setTimeout(() => {
            this.googleAnalyticsService.sendCustomGA4EventMulti('live_cii_chart_hover', {
                'customerType': this._customerType
            });
        }, 500);
    }
    
    liveCiiChartMouseOut() {
        
        //Clear timeout
        if (this._liveCiiChartTimeout !== null){
            clearTimeout(this._liveCiiChartTimeout);
            this._liveCiiChartTimeout = null;
        }
    }

    onZeroNorthClick() {

        //Send ga event
        this.googleAnalyticsService.sendCustomGA4EventMulti('live_cii_zero_north_click', {
            "customerType": this._customerType
        });

        window.open('https://zeronorth.com/emissions-api-methodology', '_blank');
    }

    //#endregion
}